import { IconLayer } from '@deck.gl/layers';
import type { LayerState, LayerFeature, StationProps } from '@vitals/shared';
import type { RendererDef } from './registry.js';

/**
 * Citi Bike stations render as little availability gauges: a donut whose colored
 * arc sweeps clockwise from 12 o'clock in proportion to bikes available, with the
 * remaining arc left as a dim "open docks" track. The arc color runs red (empty,
 * grab a dock here) → amber → green (plenty of bikes), so the whole map reads at a
 * glance. Every gauge renders at the same diameter so the map stays uniform.
 *
 * The gauges are pre-rendered once into a sprite atlas (bucketed by fullness) and
 * placed with an IconLayer — the same technique the subway bullets use — so the
 * per-frame cost is just positioning, not redrawing arcs.
 */

/** Cast a LayerFeature's props to StationProps. */
function stationProps(f: LayerFeature): StationProps {
  return f.props as unknown as StationProps;
}

const lerp = (a: number, b: number, t: number): number => a + (b - a) * t;
const clamp01 = (n: number): number => Math.min(1, Math.max(0, n));

/** Fullness ratio (bikes / capacity), clamped to [0, 1]. */
export function stationFullness(bikes: number, capacity: number): number {
  return capacity > 0 ? clamp01(bikes / capacity) : 0;
}

/**
 * Availability color for the bikes arc: red (empty) → amber (half) → green (full).
 * A perceptual two-stop ramp keyed to fullness, returned as an RGB triple.
 */
export function availabilityColor(fullness: number): [number, number, number] {
  const f = clamp01(fullness);
  const red: [number, number, number] = [235, 72, 60];
  const amber: [number, number, number] = [245, 190, 48];
  const green: [number, number, number] = [46, 214, 132];
  const [from, to, t] = f <= 0.5 ? [red, amber, f / 0.5] : [amber, green, (f - 0.5) / 0.5];
  return [
    Math.round(lerp(from[0], to[0], t)),
    Math.round(lerp(from[1], to[1], t)),
    Math.round(lerp(from[2], to[2], t)),
  ];
}

const BUCKETS = 25; // fullness resolution baked into the atlas (0%, ~4%, … 100%)

/** Snap a fullness ratio to its atlas bucket index in [0, BUCKETS - 1]. */
export function fullnessBucket(fullness: number): number {
  return Math.round(clamp01(fullness) * (BUCKETS - 1));
}

/** Uniform gauge diameter in pixels — every station renders at the same size. */
export const GAUGE_SIZE = 22;

/** Gauge diameter in pixels. Uniform across all stations. */
export function gaugeSize(_capacity: number): number {
  return GAUGE_SIZE;
}

const CELL = 72; // px per gauge sprite in the atlas
const OUTER = 30; // outer ring radius within the cell
const INNER = 18; // inner (hollow) radius — center stays transparent
const TRACK: [number, number, number] = [86, 98, 120]; // dim "open docks" arc
const OUTLINE = 'rgba(6,10,18,0.85)'; // thin dark edge so gauges pop on any basemap

export interface GaugeAtlas {
  /** PNG data URL of the sprite sheet (deck.gl IconLayer iconAtlas). */
  url: string;
  mapping: Record<string, { x: number; y: number; width: number; height: number; mask: boolean }>;
}

let cachedAtlas: GaugeAtlas | null = null;

/**
 * Build (once) a sprite atlas with one donut gauge per fullness bucket. Each cell
 * draws a dim full-circle dock track, then overdraws the bikes arc (colored by
 * fullness) sweeping clockwise from the top, plus thin dark edges for contrast.
 */
export function buildGaugeAtlas(): GaugeAtlas {
  if (cachedAtlas) return cachedAtlas;
  const cols = Math.ceil(Math.sqrt(BUCKETS));
  const rows = Math.ceil(BUCKETS / cols);
  const atlas = document.createElement('canvas');
  atlas.width = cols * CELL;
  atlas.height = rows * CELL;
  const ctx = atlas.getContext('2d')!;
  const mapping: GaugeAtlas['mapping'] = {};
  const ringRadius = (OUTER + INNER) / 2;
  const ringWidth = OUTER - INNER;
  const top = -Math.PI / 2; // 12 o'clock

  for (let i = 0; i < BUCKETS; i++) {
    const cx = (i % cols) * CELL + CELL / 2;
    const cy = Math.floor(i / cols) * CELL + CELL / 2;
    const fullness = i / (BUCKETS - 1);

    // Thin dark edges (drawn first, slightly outside the ring band) for contrast.
    ctx.lineWidth = 1.5;
    ctx.strokeStyle = OUTLINE;
    for (const r of [OUTER + 0.75, INNER - 0.75]) {
      ctx.beginPath();
      ctx.arc(cx, cy, r, 0, Math.PI * 2);
      ctx.stroke();
    }

    // Dim full-circle dock track.
    ctx.lineWidth = ringWidth;
    ctx.lineCap = 'butt';
    ctx.strokeStyle = `rgb(${TRACK[0]},${TRACK[1]},${TRACK[2]})`;
    ctx.beginPath();
    ctx.arc(cx, cy, ringRadius, 0, Math.PI * 2);
    ctx.stroke();

    // Bikes arc, colored by fullness, sweeping clockwise from the top.
    if (fullness > 0) {
      const [r, g, b] = availabilityColor(fullness);
      ctx.strokeStyle = `rgb(${r},${g},${b})`;
      ctx.lineCap = fullness < 1 ? 'round' : 'butt';
      ctx.beginPath();
      ctx.arc(cx, cy, ringRadius, top, top + fullness * Math.PI * 2);
      ctx.stroke();
    }

    mapping[`b${i}`] = {
      x: (i % cols) * CELL,
      y: Math.floor(i / cols) * CELL,
      width: CELL,
      height: CELL,
      mask: false,
    };
  }

  cachedAtlas = { url: atlas.toDataURL(), mapping };
  return cachedAtlas;
}

interface GaugeDatum {
  position: [number, number];
  icon: string;
  size: number;
  /** Original station props, carried through for the hover tooltip (tooltip.ts). */
  props: StationProps;
}

/**
 * Build an IconLayer of availability gauges for all active Citi Bike stations.
 * If the state is down or has no features, returns a layer with empty data so
 * deck.gl renders nothing without throwing.
 */
export function buildCitibikeLayer(
  state: LayerState,
  _nowSec: number,
): IconLayer<GaugeDatum> {
  const { url, mapping } = buildGaugeAtlas();
  const features: LayerFeature[] =
    state.status === 'down' ? [] : (state.features as LayerFeature[]);

  const data: GaugeDatum[] = features.map((f) => {
    const p = stationProps(f);
    const bucket = fullnessBucket(stationFullness(p.bikes, p.capacity));
    return {
      position: (f.geometry as { coordinates: [number, number] }).coordinates,
      icon: `b${bucket}`,
      size: gaugeSize(p.capacity),
      props: p,
    };
  });

  return new IconLayer<GaugeDatum>({
    id: 'citibike',
    data,
    iconAtlas: url,
    iconMapping: mapping,
    getIcon: (d) => d.icon,
    getPosition: (d) => d.position,
    getSize: (d) => d.size,
    sizeUnits: 'pixels',
    billboard: true,
    pickable: true,
  });
}

export const citibikeRenderer: RendererDef = {
  layerId: 'citibike',
  build: (state, nowSec) => buildCitibikeLayer(state, nowSec),
};
